import { useState } from 'react';
import type { FormEvent } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { clsx } from 'clsx';
import { AuthShell } from '../components/AuthShell.tsx';
import { ErrorNotice, Field, Spinner } from '../components/ui.tsx';
import { ApiRequestError, api } from '../lib/api.ts';
import { useAuth } from '../lib/auth.tsx';

interface InvitationDto {
  email: string;
  role: string;
  invitedBy: string;
  workspace: string;
}

export function InvitationPage(): React.JSX.Element {
  const { uuid = '' } = useParams<{ uuid: string }>();
  const { refresh } = useAuth();
  const navigate = useNavigate();

  const [form, setForm] = useState({ fullname: '', password: '' });
  const [error, setError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [submitting, setSubmitting] = useState(false);

  const invitation = useQuery({
    queryKey: ['invitation', uuid],
    queryFn: () => api.get<InvitationDto>(`/auth/invitation/${uuid}`),
    retry: false,
  });

  const handleSubmit = async (event: FormEvent): Promise<void> => {
    event.preventDefault();
    setError(null);
    setFieldErrors({});
    setSubmitting(true);

    try {
      await api.post('/auth/invitation/activate', { uuid, ...form });
      await refresh();
      navigate('/chats', { replace: true });
    } catch (caught) {
      if (caught instanceof ApiRequestError) {
        setError(caught.message);
        setFieldErrors(caught.fieldErrors);
      } else {
        setError('Something went wrong');
      }
    } finally {
      setSubmitting(false);
    }
  };

  if (invitation.isLoading) {
    return (
      <AuthShell title="Checking your invitation">
        <div className="flex justify-center py-4 text-brand-600">
          <Spinner className="h-6 w-6" />
        </div>
      </AuthShell>
    );
  }

  if (invitation.isError || !invitation.data) {
    const message =
      invitation.error instanceof ApiRequestError
        ? invitation.error.message
        : "This invitation doesn't exist anymore";

    return (
      <AuthShell title="Invitation unavailable">
        <ErrorNotice message={message} />
      </AuthShell>
    );
  }

  return (
    <AuthShell
      title={`Join ${invitation.data.workspace}`}
      subtitle={`${invitation.data.invitedBy} invited you as ${invitation.data.role}.`}
    >
      <form className="space-y-4" onSubmit={(event) => void handleSubmit(event)} noValidate>
        {error ? <ErrorNotice message={error} /> : null}

        <Field label="Email" htmlFor="email">
          {/* Fixed by the invitation — changing it would defeat the invite. */}
          <input id="email" className="lg-input" value={invitation.data.email} disabled />
        </Field>

        <Field label="Your name" htmlFor="fullname" error={fieldErrors.fullname}>
          <input
            id="fullname"
            className={clsx('lg-input', fieldErrors.fullname && 'lg-input-error')}
            autoComplete="name"
            autoFocus
            required
            value={form.fullname}
            onChange={(event) => {
              setForm((previous) => ({ ...previous, fullname: event.target.value }));
            }}
          />
        </Field>

        <Field
          label="Password"
          htmlFor="password"
          error={fieldErrors.password}
          hint="At least 8 characters."
        >
          <input
            id="password"
            type="password"
            className={clsx('lg-input', fieldErrors.password && 'lg-input-error')}
            autoComplete="new-password"
            required
            minLength={8}
            value={form.password}
            onChange={(event) => {
              setForm((previous) => ({ ...previous, password: event.target.value }));
            }}
          />
        </Field>

        <button type="submit" className="lg-btn-primary w-full" disabled={submitting}>
          {submitting ? <Spinner /> : null}
          Activate account
        </button>
      </form>
    </AuthShell>
  );
}
